import { useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useResource } from "@/hooks/useResource";
import { useAuth } from "@/context/AuthContext";
import { findItemByPath } from "@/config/modules";
import ResourceListPage from "@/pages/ResourceListPage";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/States";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import {
  DatabaseZap, UploadCloud, Loader2, Lock, Unlock, ShieldAlert, CheckCircle2,
  RotateCcw, FileText, ExternalLink, Building2, Wheat, Users, Package,
} from "lucide-react";

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1]);
    r.onerror = reject; r.readAsDataURL(file);
  });
}

const TYPES = [
  { value: "ingredients", label: "Ingredients" },
  { value: "recipes", label: "Recipes" },
  { value: "products", label: "Products" },
  { value: "suppliers", label: "Suppliers" },
  { value: "buyers", label: "Buyers" },
];

// Bulk extract -> review -> apply/rollback importer (moved into a tab).
function BulkImportPanel() {
  const qc = useQueryClient();
  const { role } = useAuth();
  const isSuperAdmin = role === "super_admin";
  const inputRef = useRef(null);

  const { data: cfg, refetch: refetchCfg } = useResource("/master-data-import/config");
  const { data: batchData, isLoading, isError, error, refetch } = useResource("/master-data-import/batches");
  const batches = useMemo(() => (Array.isArray(batchData) ? batchData : []), [batchData]);

  const [type, setType] = useState("ingredients");
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [applyConfirm, setApplyConfirm] = useState(false);
  const [rollbackConfirm, setRollbackConfirm] = useState(false);

  const locked = !!cfg?.locked;

  const toggleLock = async () => {
    setBusy(true);
    try {
      await api.post(`/master-data-import/config/${locked ? "unlock" : "lock"}`, {});
      toast.success(locked ? "Import unlocked" : "Import locked");
      refetchCfg();
    } catch (e) { toast.error(apiErrorMessage(e, "Could not change lock")); }
    finally { setBusy(false); }
  };

  const extract = async () => {
    if (!file) return;
    setExtracting(true);
    try {
      const contentBase64 = await toBase64(file);
      await api.post("/master-data-import/extract", { type, filename: file.name, contentBase64 });
      toast.success("Extracted to a review batch. Review, then Apply.");
      setFile(null);
      refetch();
    } catch (e) { toast.error(apiErrorMessage(e, "Extract failed")); }
    finally { setExtracting(false); }
  };

  const openBatch = async (b) => {
    setBusy(true);
    try {
      const res = await api.get(`/master-data-import/batches/${b.id}`);
      setDetail({ ...b, ...res.data });
    } catch (e) { toast.error(apiErrorMessage(e, "Could not load batch")); }
    finally { setBusy(false); }
  };

  const applyBatch = async () => {
    setApplyConfirm(false); setBusy(true);
    try {
      await api.post("/master-data-import/apply", { type: detail.type || type, batchId: detail.id });
      toast.success("Batch applied to master data.");
      setDetail(null); refetch(); qc.invalidateQueries();
    } catch (e) { toast.error(apiErrorMessage(e, "Apply failed")); }
    finally { setBusy(false); }
  };

  const rollbackBatch = async () => {
    setRollbackConfirm(false); setBusy(true);
    try {
      await api.post(`/master-data-import/batches/${detail.id}/rollback`, {});
      toast.success("Batch rolled back.");
      setDetail(null); refetch(); qc.invalidateQueries();
    } catch (e) { toast.error(apiErrorMessage(e, "Rollback failed")); }
    finally { setBusy(false); }
  };

  const viewOriginal = async (docId) => {
    try {
      const res = await api.get(`/master-data-import/documents/${docId}/original`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      window.open(url, "_blank");
    } catch (e) { toast.error(apiErrorMessage(e, "Could not open document")); }
  };

  const detailRows = detail && (detail.rows || detail.records || detail.items || detail.preview || []);

  return (
    <div className="space-y-6">
      <div className={`flex items-center justify-between rounded-lg border px-4 py-3 text-sm ${locked ? "border-rose-200 bg-rose-50 text-rose-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>
        <span className="flex items-center gap-2" data-testid="mdi-lock-status">
          {locked ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
          Bulk import is <strong>{locked ? "LOCKED" : "unlocked"}</strong>{locked && cfg?.lockedBy ? ` by ${cfg.lockedBy}` : ""}
        </span>
        {isSuperAdmin ? (
          <Button data-testid="mdi-lock-toggle" size="sm" variant="outline" className="h-8 gap-1.5" disabled={busy} onClick={toggleLock}>
            {locked ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />} {locked ? "Unlock" : "Lock"}
          </Button>
        ) : <span className="text-xs opacity-70">Only a Super Admin can change this.</span>}
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2"><DatabaseZap className="h-4 w-4 text-amber-700" /><h3 className="font-display text-base font-semibold text-stone-900">Extract a new batch</h3></div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="space-y-1">
            <Label className="text-xs">Dataset</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="mdi-type" className="h-9 w-48 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <div data-testid="mdi-dropzone" onClick={() => inputRef.current?.click()}
              className="flex cursor-pointer items-center gap-3 rounded-lg border-2 border-dashed border-stone-300 bg-stone-50/50 px-4 py-3 text-sm hover:border-amber-400">
              <UploadCloud className="h-5 w-5 text-amber-700" />
              {file ? <span className="font-medium text-stone-800">{file.name}</span> : <span className="text-muted-foreground">Select a file to extract (xlsx / pdf)</span>}
              <input ref={inputRef} data-testid="mdi-file-input" type="file" accept=".xlsx,.pdf,.csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>
          </div>
          <Button data-testid="mdi-extract" className="h-9 gap-2 bg-amber-700 hover:bg-amber-800" disabled={!file || extracting || locked} onClick={extract}>
            {extracting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Extract
          </Button>
        </div>
        {locked && <p className="mt-2 text-xs text-rose-600">Import is locked — unlock (Super Admin) to extract new batches.</p>}
      </div>

      {isLoading ? <LoadingState label="Loading batches…" /> :
        isError ? <ErrorState message={apiErrorMessage(error)} onRetry={refetch} /> :
        batches.length === 0 ? <EmptyState title="No import batches" message="Extract a file above to create a review batch. Applying a batch writes to master data; rollback reverses it." /> : (
          <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="border-b border-stone-200 bg-stone-50 text-left text-[11px] font-bold uppercase tracking-wider text-stone-500">
                <tr><th className="px-4 py-2.5">Batch</th><th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5 text-right">Records</th><th className="px-4 py-2.5">Created</th></tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {batches.map((b) => (
                  <tr key={b.id} data-testid={`mdi-batch-${b.id}`} className="cursor-pointer hover:bg-amber-50/30" onClick={() => openBatch(b)}>
                    <td className="px-4 py-2.5 font-mono text-xs text-stone-700">{b.id}</td>
                    <td className="px-4 py-2.5">{b.type}</td>
                    <td className="px-4 py-2.5">{b.status}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{b.recordCount ?? "—"}</td>
                    <td className="px-4 py-2.5 text-stone-500">{fmtDateTime(b.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full overflow-y-auto bg-white sm:max-w-2xl">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display">Review Batch — {detail.id}</SheetTitle>
                <SheetDescription className="text-xs">{detail.type} · {detail.status}</SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                {detail.summary && (
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(detail.summary).map(([k, v]) => (
                      <span key={k} className="rounded-md border border-stone-200 bg-stone-50 px-2.5 py-1 text-xs text-stone-700">{k}: <strong>{String(v)}</strong></span>
                    ))}
                  </div>
                )}
                {(detail.documentId || detail.docId) && (
                  <Button data-testid="mdi-view-original" size="sm" variant="outline" className="gap-1.5" onClick={() => viewOriginal(detail.documentId || detail.docId)}>
                    <ExternalLink className="h-3.5 w-3.5" /> View original document
                  </Button>
                )}
                {Array.isArray(detailRows) && detailRows.length > 0 ? (
                  <div className="overflow-x-auto rounded-lg border border-stone-200">
                    <table className="w-full min-w-[500px] text-xs">
                      <thead className="border-b border-stone-200 bg-stone-50 text-left font-semibold text-stone-500">
                        <tr>{Object.keys(detailRows[0]).slice(0, 6).map((k) => <th key={k} className="px-3 py-2">{k}</th>)}</tr>
                      </thead>
                      <tbody className="divide-y divide-stone-100">
                        {detailRows.slice(0, 100).map((r, i) => (
                          <tr key={i}>{Object.keys(detailRows[0]).slice(0, 6).map((k) => <td key={k} className="px-3 py-2 text-stone-700">{String(r[k] ?? "—")}</td>)}</tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-500"><FileText className="h-4 w-4" /> No preview rows available for this batch.</div>
                )}
                <div className="flex gap-2 pt-2">
                  <Button data-testid="mdi-apply" className="gap-2 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => setApplyConfirm(true)}>
                    <CheckCircle2 className="h-4 w-4" /> Apply to master
                  </Button>
                  <Button data-testid="mdi-rollback" variant="outline" className="gap-2 text-rose-600" disabled={busy} onClick={() => setRollbackConfirm(true)}>
                    <RotateCcw className="h-4 w-4" /> Rollback
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={applyConfirm} onOpenChange={setApplyConfirm}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600" /> Apply batch to master data</AlertDialogTitle>
            <AlertDialogDescription>This writes the reviewed records into the shared master data. It can be reversed with Rollback. No operational (stock/GRN/sales) transaction is created. Proceed?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mdi-apply-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="mdi-apply-confirm" className="bg-emerald-600 hover:bg-emerald-700" onClick={applyBatch}>Yes, apply</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={rollbackConfirm} onOpenChange={setRollbackConfirm}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-rose-600" /> Rollback batch</AlertDialogTitle>
            <AlertDialogDescription>This reverses the changes made by applying this batch. Proceed?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mdi-rollback-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="mdi-rollback-confirm" className="bg-rose-600 hover:bg-rose-700" onClick={rollbackBatch}>Yes, rollback</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function MasterDataSetup() {
  const { role } = useAuth();
  const [tab, setTab] = useState("suppliers");

  const cfgs = {
    suppliers: findItemByPath("/suppliers"),
    ingredients: findItemByPath("/ingredients"),
    buyers: findItemByPath("/buyers"),
    products: findItemByPath("/products"),
  };

  if (role !== "super_admin" && role !== "management") {
    return (
      <div className="space-y-6">
        <PageHeader title="Master Data Setup" subtitle="One-time master setup and controlled historical-data preparation." />
        <EmptyState title="Restricted" message="Master Data Setup is available to Super Admin / Management only." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Master Data Setup" subtitle="One-time master setup and controlled historical-data preparation. Add, edit, search, import and view IDs across all four masters." />

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList data-testid="mds-tabs" className="flex-wrap bg-stone-100">
          <TabsTrigger value="suppliers" data-testid="mds-tab-suppliers" className="gap-1.5"><Building2 className="h-3.5 w-3.5" /> Suppliers</TabsTrigger>
          <TabsTrigger value="ingredients" data-testid="mds-tab-ingredients" className="gap-1.5"><Wheat className="h-3.5 w-3.5" /> Purchase Items</TabsTrigger>
          <TabsTrigger value="buyers" data-testid="mds-tab-buyers" className="gap-1.5"><Users className="h-3.5 w-3.5" /> Buyers</TabsTrigger>
          <TabsTrigger value="products" data-testid="mds-tab-products" className="gap-1.5"><Package className="h-3.5 w-3.5" /> Selling Items</TabsTrigger>
          <TabsTrigger value="bulk" data-testid="mds-tab-bulk" className="gap-1.5"><DatabaseZap className="h-3.5 w-3.5" /> Bulk Import</TabsTrigger>
        </TabsList>

        <TabsContent value="suppliers"><ResourceListPage itemOverride={cfgs.suppliers} /></TabsContent>
        <TabsContent value="ingredients"><ResourceListPage itemOverride={cfgs.ingredients} /></TabsContent>
        <TabsContent value="buyers"><ResourceListPage itemOverride={cfgs.buyers} /></TabsContent>
        <TabsContent value="products"><ResourceListPage itemOverride={cfgs.products} /></TabsContent>
        <TabsContent value="bulk"><BulkImportPanel /></TabsContent>
      </Tabs>
    </div>
  );
}
